import React, { useCallback, useEffect, useMemo } from 'react';
import { useTracking } from 'react-tracking';
import classnames from 'classnames';
import Switch from '../../../../common/inputs/customs/switch';
import { Timer } from '../components';
import { ModalType } from '../../modals';
import styles from './index.module.scss';
import { Tooltip } from '../../../sharable/components/Tooltip';
import { Appearance, Folder, Message } from '../../state/types';
import { useTypedSelector } from 'state';

const SEVEN_MINUTES_MS = 7 * 60 * 1000;

interface Props {
  toggleModerationMode: (valuePayload?: { value: boolean; skipReleaseLock?: boolean }) => void;
  openModal: (modalType, modalProps) => void;
}

const ModerationMode: React.FC<Props> = ({ openModal, toggleModerationMode = () => {} }) => {
  const {
    lockedItems,
    itemsList,
    isModerationMode,
    pageInfo: { folder }
  } = useTypedSelector(state => state.moderation.messages);

  const tracking = useTracking();

  const isDisabledOnPending =
    folder === Folder.PENDING && itemsList.length === 0 && !isModerationMode;
  const disabled = (!isModerationMode && folder !== Folder.PENDING) || isDisabledOnPending;

  const disabledText = isDisabledOnPending
    ? 'Moderation mode is only available when there are comments in the Pending queue.'
    : 'Moderation mode is only available in the Pending queue.';

  const lockedActiveItemsNum = useMemo(() => {
    if (!isModerationMode) {
      return 0;
    }

    const source = folder === Folder.PENDING ? itemsList : lockedItems;

    return getActiveItems(source);
  }, [itemsList, lockedItems, isModerationMode, folder]);

  const onModerationModeToggleClick = value => {
    tracking.trackEvent({ eventName: 'toggle moderation mode' });
    toggleModerationMode({ value, skipReleaseLock: true });
  };

  const openModerationModeToggle = () => {
    toggleModerationMode({ value: true });
  };

  const closeModerationModeToggle = useCallback(() => {
    toggleModerationMode({ value: false });
  }, [toggleModerationMode]);

  const onCloseSessionExpiredModal = (ok: boolean) => {
    if (ok) {
      openModerationModeToggle();
    }
  };

  const onSessionElapsed = () => {
    closeModerationModeToggle();
    openModal(ModalType.MODERATION_SESSION_EXPIRED, {
      className: 'confirm-modal expired-modal',
      onClose: onCloseSessionExpiredModal
    });
  };

  useEffect(function () {
    return closeModerationModeToggle;
  }, []);

  useEffect(
    function () {
      if (isModerationMode) {
        window.addEventListener('beforeunload', closeModerationModeToggle);
        return function () {
          window.removeEventListener('beforeunload', closeModerationModeToggle);
        };
      }
    },
    [closeModerationModeToggle, isModerationMode]
  );

  return (
    <div className={styles.container}>
      <span className={classnames(styles.side, styles.title)}>
        {isModerationMode && lockedActiveItemsNum > 0 && (
          <h5>
            Moderation Session -{' '}
            <Timer duration={SEVEN_MINUTES_MS} onTimeElapsed={onSessionElapsed}>
              <span className={styles.counter}>{lockedActiveItemsNum}</span>
            </Timer>
          </h5>
        )}
      </span>

      <span className={classnames(styles.side, styles.toggle, { [styles.disabled]: disabled })}>
        <span>Moderation Mode</span>
        <Tooltip title={disabledText} useAnchorWrapper>
          <Switch
            noContent
            value={isModerationMode}
            onChange={onModerationModeToggleClick}
            disabled={disabled}
          />
        </Tooltip>
      </span>
    </div>
  );
};

export default ModerationMode;

function getActiveItems(list: Array<Message>) {
  // @ts-ignore
  return list.filter(({ clientAppearance }) => clientAppearance === Appearance.NORMAL).length;
}
